import React, { useState } from 'react';
import Button3D from '../ui/Button3D';
import AiStatusButtons from './AiStatusButtons';
import Modal3D from '../ui/Modal3D';
import TestContactsDialog from './TestContactsDialog';
import { describeTestList } from '../../lib/businessSchedule';

/**
 * BL-177 Step 2m: THE AI integration compartment. Every AI feature uses this one pattern (see Docs/Build_Execution/Platform/AI_Integration_Compartment_Pattern.md).
 *
 *   1. It opens as FOUR 3D buttons only: Off / On / Pause / Test (the current one shown pressed).
 *   2. Choosing one reveals the rest of the compartment (`children`: the feature's own options).
 *   3. On asks "are you sure" first (it speaks to real clients). Test opens the Test pop-up: phones and/or emails the AI may talk to.
 *      Pause stops it at once and keeps every setting. Off means it never replies.
 *
 * It owns no data: the caller passes the saved values and the two save functions, so the same pattern works for any feature.
 *
 * props:
 *   title, description      heading words
 *   status                  'off' | 'on' | 'pause' | 'test' (saved value)
 *   testContacts            [{id, kind, value}] the saved test list
 *   pausedLine              text for the "all AI is paused" notice, or null
 *   onSetStatus(status)     async; save the status (throw to show the error)
 *   onSaveTestContacts(entries) async; save the whole test list (throw to show the error)
 *   children                the feature's options, shown after a status is chosen
 *   defaultRevealed         tests / pages that should open already revealed
 */
export default function AiFeatureCompartment({
    id = 'ai-integration', title = 'AI integration', description, status = 'off', testContacts = [], pausedLine = null,
    onSetStatus, onSaveTestContacts, children, defaultRevealed = false,
}) {
    const [revealed, setRevealed] = useState(defaultRevealed);
    const [dialog, setDialog] = useState(null);   // 'confirm-on' | 'test'
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    async function run(action) {
        setBusy(true);
        setError('');
        try {
            await action();
            setRevealed(true);
            setDialog(null);
        } catch (failure) {
            setError(failure?.message || 'That could not be saved. Nothing changed.');
        } finally {
            setBusy(false);
        }
    }

    function choose(next) {
        setError('');
        if (next === 'on' && status !== 'on') { setDialog('confirm-on'); return; }
        // Test with a list already saved just reveals (the summary has Edit list); a first Test asks who the AI may talk to.
        if (next === 'test') { if (status === 'test' && testContacts.length) setRevealed(true); else setDialog('test'); return; }
        if (next === status) { setRevealed(true); return; }
        run(() => onSetStatus(next));
    }

    return (
        <section id={id} aria-labelledby={`${id}-title`} className="ia-d2 ia-d2-card space-y-4 rounded-xl p-4" data-testid="ai-compartment" data-ai-status={status}>
            <div>
                <h3 id={`${id}-title`} className="text-[17px] font-bold text-white">{title}</h3>
                {description && <p className="mt-1 text-sm text-[#C7D2E8]">{description}</p>}
            </div>
            <AiStatusButtons value={status} onChoose={choose} disabled={busy} label={`${title} status`} />
            {error && <p role="alert" className="text-sm font-bold text-[#FCA5A5]">{error}</p>}

            {revealed && (
                <div className="space-y-4 border-t border-[#33415E] pt-4" data-testid="ai-compartment-revealed">
                    {pausedLine && <p role="status" className="rounded-lg border border-amber-500 bg-[#2A1E08] px-3 py-2 text-sm font-bold text-white">{pausedLine}</p>}
                    {status === 'off' && <p className="text-sm text-[#C7D2E8]">Off: the assistant never replies. Your settings below are kept.</p>}
                    {status === 'pause' && <p className="text-sm text-[#C7D2E8]">Paused: the assistant has stopped replying. Your settings below are kept. Choose On or Test to start it again.</p>}
                    {status === 'test' && (
                        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#2DD4BF] bg-[#0A1C24] px-3 py-2 text-sm text-white" data-testid="ai-test-summary">
                            <span className="flex-1">
                                <strong>Test:</strong> replying only to {testContacts.length ? describeTestList(testContacts) : 'nobody yet'}. Everyone else gets no AI reply.
                            </span>
                            <Button3D variant="pill" onClick={() => setDialog('test')}>Edit list</Button3D>
                        </div>
                    )}
                    {children}
                </div>
            )}

            {dialog === 'confirm-on' && (
                <Modal3D
                    title="Turn the assistant on?"
                    subtitle="It will reply to real clients."
                    stripe="orange"
                    portal
                    testId="ai-on-dialog"
                    dismissible={!busy}
                    onClose={() => setDialog(null)}
                    actions={(
                        <>
                            <Button3D type="button" onClick={() => setDialog(null)} disabled={busy}>Not yet</Button3D>
                            <Button3D variant="go" onClick={() => run(() => onSetStatus('on'))} disabled={busy}>Turn on</Button3D>
                        </>
                    )}
                >
                    <p className="text-sm">When it is On, the assistant replies to <strong>real clients</strong> who text, WhatsApp or email you. To try it safely first, choose Test instead.</p>
                </Modal3D>
            )}
            {dialog === 'test' && (
                <TestContactsDialog
                    contacts={testContacts}
                    busy={busy}
                    onClose={() => setDialog(null)}
                    onSave={(entries) => run(async () => { await onSaveTestContacts(entries); await onSetStatus('test'); })}
                />
            )}
        </section>
    );
}
